import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import { $computeTableMapSkipCellCheck, $isTableNode } from '@lexical/table';
import type { LexicalEditor, NodeKey } from 'lexical';
import { useState } from 'react';

import { usePointerTracking } from '../../hooks/usePointerTracking';
import { EditorPortal } from '../../ui/EditorPortal';
import { $getNodeOfType } from '../../utils/nodes';
import { trackHorizontalDrag } from '../../utils/trackHorizontalDrag';
import { $getCellFromDOM } from './tableSelection';

const EDGE_TOLERANCE = 4;
const MIN_COLUMN_WIDTH = 40;

/** A column border under the pointer: `column` is the column to its left. */
type Edge = { tableKey: NodeKey; column: number; x: number; top: number; height: number };

/** Rendered widths of every column, measured from single-span cells. */
const $measureColumnWidths = (editor: LexicalEditor, tableKey: NodeKey): number[] | null => {
  const table = $getNodeOfType(tableKey, $isTableNode);
  if (!table) return null;

  const [map] = $computeTableMapSkipCellCheck(table, null, null);
  const columnCount = map[0]?.length ?? 0;
  const tableWidth = editor.getElementByKey(tableKey)?.getBoundingClientRect().width ?? 0;

  return Array.from({ length: columnCount }, (_, column) => {
    const single = map.find((row) => row[column]?.cell.getColSpan() === 1)?.[column];
    const element = single && editor.getElementByKey(single.cell.getKey());
    return element ? element.getBoundingClientRect().width : tableWidth / columnCount;
  });
};

/**
 * Drag a column border to resize, the way Froala's table resizer works.
 * - Width moves between the two neighbouring columns, so the table keeps its full width.
 * - Widths are stored as `colWidths` (relative px); saved HTML carries them as percentages.
 */
export const TableColumnResizerPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const editable = useLexicalEditable();
  const [edge, setEdge] = useState<Edge | null>(null);
  const [dragX, setDragX] = useState<number | null>(null);

  usePointerTracking(
    {
      onMove: (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('[data-table-resizer]')) return;

        const cellElement = target?.closest('td, th');
        const cellRect = cellElement?.getBoundingClientRect();
        const nearRight = !!cellRect && Math.abs(event.clientX - cellRect.right) <= EDGE_TOLERANCE;
        const nearLeft = !!cellRect && Math.abs(event.clientX - cellRect.left) <= EDGE_TOLERANCE;
        if (!cellElement || !cellRect || !editor.getRootElement()?.contains(cellElement) || (!nearRight && !nearLeft)) {
          setEdge(null);
          return;
        }

        setEdge(
          editor.read(() => {
            const found = $getCellFromDOM(cellElement);
            const tableElement = cellElement.closest('table');
            if (!found || !tableElement) return null;
            const { table, rect } = found;

            // The table's outer borders aren't resizable; it always fills the editor
            const column = nearRight ? rect.columnIndex + rect.colSpan - 1 : rect.columnIndex - 1;
            if (column < 0 || column >= table.getColumnCount() - 1) return null;

            const tableRect = tableElement.getBoundingClientRect();
            return { tableKey: table.getKey(), column, x: nearRight ? cellRect.right : cellRect.left, top: tableRect.top, height: tableRect.height };
          }),
        );
      },
      onScroll: () => setEdge(null),
    },
    editable,
  );

  if (!edge || !editable) return null;

  const startDrag = (event: Parameters<typeof trackHorizontalDrag>[0]) => {
    const widths = editor.read(() => $measureColumnWidths(editor, edge.tableKey));
    if (!widths) return;
    const left = widths[edge.column];
    const right = widths[edge.column + 1];
    let delta = 0;

    trackHorizontalDrag(
      event,
      (deltaX) => {
        delta = Math.min(right - MIN_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH - left, deltaX));
        setDragX(edge.x + delta);
      },
      () => {
        setDragX(null);
        setEdge(null);
        if (delta === 0) return;

        const next = widths.map((width, index) =>
          Math.round(index === edge.column ? width + delta : index === edge.column + 1 ? width - delta : width),
        );
        editor.update(() => $getNodeOfType(edge.tableKey, $isTableNode)?.setColWidths(next));
      },
    );
  };

  return (
    <EditorPortal
      data-table-resizer
      aria-hidden
      className="group z-popover fixed flex w-2 cursor-col-resize touch-none justify-center"
      style={{ left: (dragX ?? edge.x) - 4, top: edge.top, height: edge.height }}
      onPointerDown={startDrag}
    >
      <div className={`bg-accent-1-100 h-full w-0.5 ${dragX === null ? 'opacity-0 group-hover:opacity-100' : ''}`} />
    </EditorPortal>
  );
};
